import { Viewer, Scene, CubeGeometry, RectilinearView, RectilinearViewParams } from 'marzipano';
import { BehaviorSubject, Observable } from 'rxjs';
import { inject } from '@angular/core';
import { LoggingService  } from '@newroom-connect/library/services';

import { MarzipanoImageLoader } from './marzipano-image-loader.model';

export interface CubemapFaces {
  px: string;
  nx: string;
  py: string;
  ny: string;
  pz: string;
  nz: string;
}

export interface IMarzipanoLoadingProgress {
  loaded: number;
  total: number;
  percentage: number;
  status: 'idle' | 'loading' | 'success' | 'error';
}

export interface IMarzipanoSceneConfig {
  crossOrigin?: string;
  withCredentials?: boolean;
  initialView?: RectilinearViewParams;
  maxResolution?: number;
  maxHFov?: number;
  maxVFov?: number;
}

/**
 * MarzipanoCubemapLoader handles the loading of cubemap scenes with real progress tracking.
 * Based on the Three.js CubemapLoader but optimized for Marzipano's tile-based approach.
 *
 * This loader provides:
 * - Individual face URL loading with credentials support
 * - Real progress tracking for each face
 * - Proper Marzipano scene creation following official documentation
 * - Compatible with existing CubemapFaces interface.
 *
 * Based on Marzipano documentation: https://www.marzipano.net/docs.html#create-scene.
 */
export class MarzipanoCubemapLoader {
  public static readonly DEFAULT_VIEWER_LIMIT = {
    maxResolution: 2048,  // Higher resolution for better quality
    maxVFov: 100 * Math.PI / 180,  // Optimized vertical FOV
    maxHFov: 140 * Math.PI / 180   // Wider horizontal FOV
  };
  private readonly logger = inject(LoggingService);

  private progressSubject$: BehaviorSubject<IMarzipanoLoadingProgress>;
  private readonly totalTiles = 6;

  /**
   * @constructor
   *
   */
  constructor() {
    this.progressSubject$ = new BehaviorSubject<IMarzipanoLoadingProgress>({
      loaded: 0,
      total: this.totalTiles,
      percentage: 0,
      status: 'idle'
    });
  }

  /**
   * Load a cubemap scene from the provided face URLs with progress tracking.
   * Follows Marzipano's official documentation for cube scene creation.
   *
   * @param viewer - The Marzipano viewer instance.
   * @param faces - The cubemap face URLs.
   * @param config - Optional configuration for the scene.
   * @param config.crossOrigin - Cross-origin setting for image loading.
   * @param config.withCredentials - Whether to send credentials with requests.
   * @param config.initialView - Initial view parameters.
   * @param config.maxResolution - Maximum face resolution for limiter.
   * @param config.maxFov - Maximum field of view in radians.
   *
   * @returns Promise that resolves with the loaded Marzipano Scene.
   */
  public async loadCubemap(viewer: Viewer, faces: CubemapFaces, config: IMarzipanoSceneConfig = {}): Promise<Scene> {
    const {
      initialView = { yaw: 0, pitch: 0, fov: Math.PI / 3 },
      maxResolution = MarzipanoCubemapLoader.DEFAULT_VIEWER_LIMIT.maxResolution,
      maxHFov = MarzipanoCubemapLoader.DEFAULT_VIEWER_LIMIT.maxHFov,
      maxVFov = MarzipanoCubemapLoader.DEFAULT_VIEWER_LIMIT.maxVFov
    } = config;

    this.logger.debug('🚀 MarzipanoCubemapLoader: Starting cubemap load', { faces, config });

    // Initialize progress tracking
    this.resetLoadingProgress();

    try {
      // Create Marzipano scene components following official documentation
      const scene = await this.createMarzipanoScene(viewer, faces, {
        initialView,
        maxResolution,
        maxHFov,
        maxVFov
      });

      // Completion is handled when all first-level tiles report loaded.

      this.logger.debug('✅ Marzipano cubemap loaded successfully');
      return scene;
    } catch (error) {
      this.handleLoadingError();
      this.logger.error('❌ Failed to load Marzipano cubemap:', error);
      throw error;
    }
  }

  /**
   * Get an observable stream of loading progress updates.
   *
   * @returns Observable that emits progress updates.
   */
  public watchLoadingProgress$(): Observable<IMarzipanoLoadingProgress> {
    return this.progressSubject$.asObservable();
  }

  /** Clean up resources and complete the progress subject. */
  public disposeProgressSubject(): void {
    if (!this.progressSubject$.closed) {
      this.progressSubject$.complete();
    }
  }

  /**
   * Create a Marzipano scene with optimized multi-level geometry for progressive loading.
   * Reference: https://www.marzipano.net/docs.html#create-scene.
   *
   * @param viewer - The Marzipano viewer instance.
   * @param faces - Cubemap face URLs.
   * @param sceneConfig - Scene configuration.
   * @returns Promise that resolves with the created scene.
   */
  private async createMarzipanoScene(viewer: Viewer, faces: CubemapFaces, sceneConfig: IMarzipanoSceneConfig): Promise<Scene> {
    this.logger.debug('🏗️ Creating optimized Marzipano scene with progressive loading:', sceneConfig);

    // Define multi-level geometry for progressive loading
    // This enables immediate display of low-res tiles while high-res tiles load
    const levels = [
      { tileSize: 256, size: 256 },   // Level 0: Fast initial load (~1-2MB total)
      { tileSize: 512, size: 512 },   // Level 1: Medium quality (~4-6MB total)
      { tileSize: 1024, size: 1024 }, // Level 2: High quality (~8-12MB total)
      { tileSize: 2048, size: 2048 }  // Level 3: Full quality (~16-24MB total)
    ];

    // Create cube geometry with progressive levels
    const geometry = new CubeGeometry(levels);

    // Create view with optimized limits for better performance
    const limiter = RectilinearView.limit.traditional(
      sceneConfig.maxResolution || MarzipanoCubemapLoader.DEFAULT_VIEWER_LIMIT.maxResolution,
      sceneConfig.maxVFov || MarzipanoCubemapLoader.DEFAULT_VIEWER_LIMIT.maxVFov,
      sceneConfig.maxHFov || MarzipanoCubemapLoader.DEFAULT_VIEWER_LIMIT.maxHFov
    );

    const view = new RectilinearView(sceneConfig.initialView, limiter);
    const stage = viewer.stage();

    const imageLoader = new MarzipanoImageLoader(this.incrementLoaded.bind(this));

    // Patch image loader in stage to handle authentication when fetching the images.
    imageLoader.patchStageForAuthentication(stage);

    // Create image source with individual face URLs
    const source = imageLoader.createImageSource(faces);

    // Create scene with performance-optimized settings
    const scene = viewer.createScene({
      source: source,
      geometry: geometry,
      view: view,
      pinFirstLevel: true  // Pin first level for immediate display
    });

    this.logger.debug('🎬 Optimized Marzipano scene created, switching with fast transition');

    // Switch to scene with optimized transition for faster loading
    await new Promise<void>((resolve) => {
      scene.switchTo({
        transitionDuration: 300  // Reduced from 1000ms for faster perceived loading
      });

      // Reduced wait time for faster scene switching
      setTimeout(resolve, 400);
    });

    return scene;
  }

  /**
   * Reset progress tracking for a new loading operation.
   */
  private resetLoadingProgress(): void {
    const resetProgress: IMarzipanoLoadingProgress = {
      loaded: 0,
      total: this.totalTiles,
      percentage: 0,
      status: 'loading'
    };

    this.progressSubject$.next(resetProgress);
  }

  /**
   *
   *
   */
  private incrementLoaded(): void {
    const current = this.progressSubject$.value;

    if (current.status !== 'loading') {
      return;
    }

    const loaded = Math.min(current.loaded + 1, current.total);
    const percentage = Math.round((loaded / current.total) * 100);
    const status = loaded === current.total ? 'success' as const : 'loading' as const;

    this.progressSubject$.next({ ...current, loaded, percentage, status });
  }

  /**
   * Mark loading as complete.
   */
  private handleLoadingComplete(): void {
    const currentProgress = this.progressSubject$.value;
    const completedProgress: IMarzipanoLoadingProgress = {
      ...currentProgress,
      status: 'success',
      loaded: currentProgress.total,
      percentage: 100
    };

    this.progressSubject$.next(completedProgress);
  }

  /**
   * Mark loading as failed.
   */
  private handleLoadingError(): void {
    const currentProgress = this.progressSubject$.value;
    const errorProgress: IMarzipanoLoadingProgress = {
      ...currentProgress,
      status: 'error'
    };

    this.progressSubject$.next(errorProgress);
  }
}
